import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getConfig } from '../runtimeConfig';
import { startProgress, stopProgress } from '../components/common/TopProgressBar';
import { swrGet, invalidateByPrefix, clearPersistentApiCache } from './apiCache';
import { getGlobalFilter } from '../contexts/GlobalFilterContext';

// Global drill-down filter (Jun 03 2026) — endpoints that accept warehouses/
// customers narrowing. When a warehouse/customer is selected on the Dashboard,
// we inject those params into these data GETs so EVERY module follows the same
// selection end-to-end, without editing each page. Injected into config.params
// BEFORE the SWR cache key is computed so each filter gets its own cache entry.
// NOT applied to /warehouses or /customers (those ARE the pickers) or to the
// dashboard (the picker surface itself).
// Every data-display path that should follow the drill-down. /data covers the
// inventory KPI strip (/data/stats); /stock-movement covers the movement panel
// (which uses the singular warehouse/customer param — handled below).
const GLOBAL_FILTER_PATHS = [
  '/inventory', '/inbound', '/outbound', '/expiry', '/pallets',
  '/alerts', '/dashboard', '/data', '/stock-movement',
  // Config/transactional sections now follow the drill-down too.
  '/documents', '/appointments', '/inbound-bookings', '/activity-rules',
  '/transport', '/project-leaders',
];
function applyGlobalFilter(url: string, config?: any): any {
  try {
    const path = (url || '').split('?')[0];
    if (!GLOBAL_FILTER_PATHS.some(p => path === p || path.startsWith(p + '/') || path.startsWith(p + '?') || path === p)) {
      return config;
    }
    const f = getGlobalFilter();
    if (!f || (!f.warehouse && !f.customer)) return config;
    const params = { ...(config?.params || {}) };
    // Don't override an explicit per-call selection if one was passed.
    // Inject BOTH the plural (warehouses/customers) and singular (warehouse/
    // customer) param names so every endpoint signature is covered — some take
    // plural (e.g. /data/stats, /dashboard), some only singular (/stock-movement).
    // Backends that read both prefer the plural, so sending both is safe.
    if (f.warehouse && params.warehouses == null && params.warehouse == null && params.warehouse_code == null) {
      params.warehouses = f.warehouse;
      params.warehouse = f.warehouse;
      params.warehouse_code = f.warehouse;   // Documents/Appointments use this name
    }
    if (f.customer && params.customers == null && params.customer == null && params.customer_code == null) {
      params.customers = f.customer;
      params.customer = f.customer;
      params.customer_code = f.customer;
    }
    return { ...config, params };
  } catch {
    return config;
  }
}

// ─────────────────────────────────────────────────────────────────
// Global response cache — eliminates "Loading..." flash on tab revisit.
//
// All GET caching now lives in `./apiCache.ts` (L1 RAM + L2 IndexedDB +
// in-flight dedup + 7-day disk persistence). This file just wires the
// axios client into that layer. The previous in-line cache (Map-only,
// no persistence) was a strict subset of the new layer.
//
// Pages don't need to know this exists — it's transparent to every
// `api.client.get(...)` call. One layer fixes all 17 pages at once.
// ─────────────────────────────────────────────────────────────────

/** Manual invalidation — call after mutations (POST/PUT/DELETE).
 *  Kept as a re-export for any external callers still importing it. */
export function invalidateResponseCache(urlPrefix?: string): void {
  if (!urlPrefix) {
    // Wipe everything — also drops disk cache. Used by logout, etc.
    void clearPersistentApiCache();
    return;
  }
  invalidateByPrefix(urlPrefix);
}

class ApiService {
  private client: AxiosInstance;
  private refreshing: Promise<string> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: getConfig().REACT_APP_API || 'https://api.whalasi.xyz/api',
      timeout: 180000, // 3 min — DB2 cold-start queries can take ≥30s
      headers: { 'Content-Type': 'application/json' }
    });

    // ─── Wire client.get into the SWR cache layer ───
    // Stash the raw axios GET so `apiCache.swrGet` can call through it
    // without re-entering our wrapper (would infinite-loop otherwise).
    // The wrapper handles: L1 RAM + L2 IndexedDB lookup, in-flight
    // dedup, background revalidate. Auto-bypassed for blob downloads
    // and `_noCache` opt-outs (see apiCache.ts).
    const originalGet = this.client.get.bind(this.client);
    (this.client as any).__rawGet = originalGet;
    (this.client as any).get = (url: string, config?: any) => {
      // Inject the global drill-down filter (warehouse/customer) into data GETs
      // before the cache key is computed, so the whole app follows one selection.
      return swrGet(this.client, url, applyGlobalFilter(url, config));
    };

    // After any mutation, drop cached GETs whose URL starts with the
    // same base path. Example:
    //   POST /inbound-bookings        → invalidates /inbound-bookings*
    //   PUT  /inbound-bookings/42     → invalidates /inbound-bookings*
    //   PATCH /docs/3/categories      → invalidates /docs/3/categories*
    //                                    AND /docs* (we walk up one level)
    // Walking up one level catches the common pattern where a mutation
    // on /{resource}/{id} should refresh the parent /{resource} list.
    const invalidate = (url: string) => {
      try {
        const path = url.replace(/^https?:\/\/[^/]+/, '').replace(/\?.*$/, '');
        invalidateByPrefix(path);
        const parent = path.split('/').slice(0, -1).join('/');
        if (parent && parent !== path) invalidateByPrefix(parent);
      } catch { /* non-fatal */ }
    };
    const wrap = (method: 'post' | 'put' | 'patch' | 'delete') => {
      const orig = (this.client as any)[method].bind(this.client);
      (this.client as any)[method] = async (url: string, ...rest: any[]) => {
        const r = await orig(url, ...rest);
        invalidate(url);
        return r;
      };
    };
    wrap('post'); wrap('put'); wrap('patch'); wrap('delete');

    // Request interceptor - dynamically set baseURL from config + add auth token
    this.client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      // Always read the latest config (handles race condition where config.json loads after module init)
      const latestBase = getConfig().REACT_APP_API || 'https://api.whalasi.xyz/api';
      if (latestBase && config.baseURL !== latestBase) {
        config.baseURL = latestBase;
      }
      const token = localStorage.getItem('accessToken');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Drive global top progress bar. The matching stopProgress runs on
      // BOTH the fulfilled and rejected response paths below so aborts
      // and errors never leak the ref-counter.
      try { startProgress(); } catch { /* noop */ }
      return config;
    });

    // Response interceptor - handle token refresh
    this.client.interceptors.response.use(
      response => {
        try { stopProgress(); } catch { /* noop */ }
        return response;
      },
      async error => {
        try { stopProgress(); } catch { /* noop */ }
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            if (!this.refreshing) {
              this.refreshing = this.refreshToken();
            }
            const newToken = await this.refreshing;
            this.refreshing = null;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            this.refreshing = null;
            this.logout();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) throw new Error('No refresh token');
    
    const baseURL = getConfig().REACT_APP_API || 'https://api.whalasi.xyz/api';
    const response = await axios.post(`${baseURL}/auth/refresh`, { refreshToken });
    const { accessToken, refreshToken: newRefreshToken } = response.data.tokens;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    return accessToken;
  }

  // AUTH
  async login(username: string, password: string) {
    const response = await this.client.post('/auth/login', { username, password });
    const { user, tokens } = response.data;
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    return response.data;
  }

  logout() {
    this.client.post('/auth/logout').catch(() => {});
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  async forgotPassword(email: string) {
    return (await this.client.post('/auth/forgot-password', { email })).data;
  }

  async resetPassword(email: string, otp_code: string, new_password: string) {
    return (await this.client.post('/auth/reset-password', { email, otp_code, new_password })).data;
  }

  async getMe() { return (await this.client.get('/auth/me')).data; }
  async register(userData: any) { return (await this.client.post('/auth/register', userData)).data; }
  async getUsers(params?: { limit?: number; offset?: number }) {
    return (await this.client.get('/auth/users', { params })).data;
  }
  // Scope-safe staff directory for the team-member picker (id/display_name/role/
  // avatar only — no sensitive fields). Available to internal staff, not just
  // admins, so non-admin team owners aren't blocked. Backend: GET /auth/users/pickable.
  async getPickableUsers() {
    return (await this.client.get('/auth/users/pickable')).data;
  }
  async updateUser(id: number, data: any) { return (await this.client.put(`/auth/users/${id}`, data)).data; }
  // Admin-only — flips is_active for a user. Backend: PUT /auth/users/{id}/toggle
  async toggleUserActive(id: number) {
    return (await this.client.put(`/auth/users/${id}/toggle`)).data;
  }
  // Admin-only — hard-deletes a user. Backend: DELETE /auth/users/{id}
  async deleteUser(id: number) {
    return (await this.client.delete(`/auth/users/${id}`)).data;
  }

  // HALLEY AI CHAT
  // Routed through the authenticated `client` so it gets the SAME request
  // interceptor (auto Bearer token) and response interceptor (401 → refresh
  // → retry) as every other call. Previously HalleyAdvanced used a bare
  // axios.post with a manually-read accessToken and NO refresh, so a stale
  // token silently produced empty/zero answers on web while mobile worked.
  async chat(message: string, extras: Record<string, any> = {}) {
    // Fold the global drill-down filter into the chat so Halley answers scoped
    // to the active warehouse/customer (Jun 03 2026). Backend narrows its scope
    // by these — only ever within the user's authorized access. An explicit
    // `extras.warehouse`/`extras.customer` (if a caller passes one) wins.
    const gf = getGlobalFilter();
    const focus: Record<string, any> = {};
    if (gf.warehouse && extras.warehouse == null) focus.warehouse = gf.warehouse;
    if (gf.customer && extras.customer == null) focus.customer = gf.customer;
    return (await this.client.post('/chat', { message, ...focus, ...extras }, { timeout: 120000 })).data;
  }
  // Fresh (uncached) liveness probe for the Halley connection badge.
  async chatHealth() {
    const rawGet = (this.client as any).__rawGet || this.client.get.bind(this.client);
    return (await rawGet('/health', { timeout: 5000 })).data;
  }

  // DASHBOARD DATA (existing endpoints)
  async getHealth() { return (await this.client.get('/health')).data; }
  async getDataStats() { return (await this.client.get('/data/stats')).data; }
  async getWarehouses() { return (await this.client.get('/warehouses')).data; }
  async getCustomers(limit = 100) { return (await this.client.get('/customers', { params: { limit } })).data; }
  // Canonical DB2-backed customer dropdown — used by Bookings, Appointments,
  // Announcements, Project Leaders. Single source of truth.
  async getCustomersDropdown() { return (await this.client.get('/customers/dropdown')).data; }
  // Canonical DB2-backed warehouse dropdown — same pattern.
  async getWarehousesDropdown() { return (await this.client.get('/warehouses/dropdown')).data; }
  async getExpiry() { return (await this.client.get('/expiry')).data; }
  async getPallets() { return (await this.client.get('/pallets')).data; }
  async getCapacity() { return (await this.client.get('/capacity')).data; }
  async getInventory() { return (await this.client.get('/inventory')).data; }
  async getInbound() { return (await this.client.get('/inbound')).data; }
  async getMovementTrends() { return (await this.client.get('/movement-trends')).data; }
  async refreshData() { return (await this.client.post('/data/refresh')).data; }

  // DASHBOARD V2 — reworked, scope-enforced (per user approval April 2026)
  // Pallets  = DB2 BMZULE  +  DB2 BMLENR  +  Azure WeeklyClientEntries
  // Types    = PLPSKOP.LKFACH (shelf/fach), not bin/floor buckets
  // Scoping  = resolve_scope(user) — customers only see their mapped BMFIRMs
  async getDashboardV2Overview(opts: { live?: boolean; fresh?: boolean } = {}) {
    // `fresh=1` makes the backend bypass its 300s overview cache and rebuild
    // from the current snapshot — so the manual Refresh button actually pulls
    // new data instead of returning the cached payload. (FIX Jun 04 2026)
    const params: Record<string, number> = {};
    if (opts.live) params.live = 1;
    if (opts.fresh) params.fresh = 1;
    return (await this.client.get('/dashboard/v2/overview', { params })).data;
  }
  async getDashboardV2DrillWarehouse(warehouse: string) {
    return (await this.client.get(`/dashboard/v2/drill/warehouse/${encodeURIComponent(warehouse)}`)).data;
  }
  async getDashboardV2DrillContainer(containerNo: string) {
    return (await this.client.get(`/dashboard/v2/drill/container/${encodeURIComponent(containerNo)}`)).data;
  }
  async getDashboardV2TransportSummary() {
    return (await this.client.get('/dashboard/v2/transport/summary')).data;
  }

  // Inbound / Outbound drill-downs — scope-enforced server-side.
  async getDashboardV2DrillInbound(params: {
    warehouse?: string; customer?: string; supplier?: string;
    container?: string; dateFrom?: string; dateTo?: string; limit?: number;
  } = {}) {
    const q: Record<string, unknown> = {};
    if (params.warehouse) q.warehouse = params.warehouse;
    if (params.customer) q.customer = params.customer;
    if (params.supplier) q.supplier = params.supplier;
    if (params.container) q.container = params.container;
    if (params.dateFrom) q.date_from = params.dateFrom;
    if (params.dateTo) q.date_to = params.dateTo;
    if (params.limit) q.limit = params.limit;
    return (await this.client.get('/dashboard/v2/drill/inbound', { params: q })).data;
  }

  async getDashboardV2DrillOutbound(params: {
    warehouse?: string; customer?: string; doNumber?: string;
    dateFrom?: string; dateTo?: string; limit?: number;
  } = {}) {
    const q: Record<string, unknown> = {};
    if (params.warehouse) q.warehouse = params.warehouse;
    if (params.customer) q.customer = params.customer;
    if (params.doNumber) q.do_number = params.doNumber;
    if (params.dateFrom) q.date_from = params.dateFrom;
    if (params.dateTo) q.date_to = params.dateTo;
    if (params.limit) q.limit = params.limit;
    return (await this.client.get('/dashboard/v2/drill/outbound', { params: q })).data;
  }

  async getDashboardV2DrillInboundGr(grNumber: string) {
    return (await this.client.get(
      `/dashboard/v2/drill/inbound/${encodeURIComponent(grNumber)}`,
    )).data;
  }

  async getDashboardV2DrillOutboundDo(doNumber: string) {
    return (await this.client.get(
      `/dashboard/v2/drill/outbound/${encodeURIComponent(doNumber)}`,
    )).data;
  }

  // Top-moving items + per-item movement drill
  async getTopInboundItems(opts: { days?: number; limit?: number } = {}) {
    const params: any = {};
    if (opts.days)  params.days  = opts.days;
    if (opts.limit) params.limit = opts.limit;
    return (await this.client.get('/dashboard/v2/top-inbound-items', { params })).data;
  }
  async getTopOutboundItems(opts: { days?: number; limit?: number } = {}) {
    const params: any = {};
    if (opts.days)  params.days  = opts.days;
    if (opts.limit) params.limit = opts.limit;
    return (await this.client.get('/dashboard/v2/top-outbound-items', { params })).data;
  }
  async getItemMovementHistory(itemCode: string, opts: { days?: number } = {}) {
    const params: any = {};
    if (opts.days) params.days = opts.days;
    return (await this.client.get(
      `/dashboard/v2/item-movement/${encodeURIComponent(itemCode)}`,
      { params },
    )).data;
  }

  // TRANSPORT
  async createBooking(data: any) { return (await this.client.post('/transport/bookings', data)).data; }
  async getBookings(params?: any) { return (await this.client.get('/transport/bookings', { params })).data; }
  async getBooking(id: number) { return (await this.client.get(`/transport/bookings/${id}`)).data; }
  async updateBookingStatus(id: number, data: any) { return (await this.client.put(`/transport/bookings/${id}/status`, data)).data; }
  async getActiveTransports() { return (await this.client.get('/transport/active')).data; }
  async getTransportStats() { return (await this.client.get('/transport/stats')).data; }
  async getGates(warehouse_code?: string) { return (await this.client.get('/transport/gates', { params: { warehouse_code } })).data; }
  async getTransportHistory(days = 30) { return (await this.client.get('/transport/history', { params: { days } })).data; }

  // APPOINTMENTS
  async getAppointmentSlots(date: string, warehouse_code: string) { return (await this.client.get('/appointments/slots', { params: { date, warehouse_code } })).data; }
  async createAppointment(data: any) { return (await this.client.post('/appointments', data)).data; }
  async getAppointments(params?: any) { return (await this.client.get('/appointments', { params })).data; }
  async updateAppointment(id: number, data: any) { return (await this.client.put(`/appointments/${id}`, data)).data; }
  async approveAppointment(id: number) { return (await this.client.put(`/appointments/${id}/approve`)).data; }
  async rejectAppointment(id: number) { return (await this.client.put(`/appointments/${id}/reject`)).data; }
  async deleteAppointment(id: number) { return (await this.client.delete(`/appointments/${id}`)).data; }

  // EXPIRY & ALARMS
  async getExpiryData(limit?: number, customer?: string) { return (await this.client.get('/expiry', { params: { limit, customer } })).data; }

  // DOCUMENTS
  async uploadDocument(formData: FormData) { return (await this.client.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })).data; }
  async getDocuments(params?: any) { return (await this.client.get('/documents', { params })).data; }
  async downloadDocument(id: number) { return (await this.client.get(`/documents/${id}/download`, { responseType: 'blob' })).data; }
  async deleteDocument(id: number) { return (await this.client.delete(`/documents/${id}`)).data; }
  async sendDocument(id: number, data: { emails: string[]; note?: string; resend_previous?: boolean }) { return (await this.client.post(`/documents/${id}/send`, data)).data; }
  async getDocumentCategories() { return (await this.client.get('/documents/categories/list')).data; }

  // NOTIFICATIONS
  async getNotifications() { return (await this.client.get('/auth/notifications')).data; }
  async markNotificationRead(id: number) { return (await this.client.put(`/auth/notifications/${id}/read`)).data; }
  async markAllNotificationsRead() { return (await this.client.post('/auth/notifications/read-all')).data; }
  // Notification preferences (snake_case fields match backend
  // NotificationPreferencesUpdate model). Used by SettingsPage's
  // Notifications tab — mobile has had this for a while; web added May 2026.
  async getNotificationPreferences() {
    return (await this.client.get('/auth/notification-preferences')).data;
  }
  async updateNotificationPreferences(prefs: any) {
    return (await this.client.put('/auth/notification-preferences', prefs)).data;
  }
  // Support tickets — backend creates an HALA-IT-YYYYMMDD-NNNN reference
  // and emails the configured SUPPORT_EMAIL. 502 on email-delivery failure.
  async createSupportTicket(body: { subject: string; message: string; priority?: string; topic?: string }) {
    return (await this.client.post('/support/tickets', body)).data;
  }
  // On-demand report generation — emails an Excel report to the caller.
  async getReportTypes() {
    return (await this.client.get('/reports/types')).data;
  }
  async generateReport(body: { type: string; target_id?: string; recipients?: string[]; to_email?: string }) {
    return (await this.client.post('/reports/generate', body)).data;
  }

  // REPORTS (existing)
  async getMailSchedules() { return (await this.client.get('/inventory-mail/schedules')).data; }
  async createMailSchedule(data: any) { return (await this.client.post('/inventory-mail/schedules', data)).data; }
  async updateMailSchedule(id: number, data: any) { return (await this.client.patch(`/inventory-mail/schedules/${id}`, data)).data; }
  async deleteMailSchedule(id: number) { return (await this.client.delete(`/inventory-mail/schedules/${id}`)).data; }
  async runMailSchedule(id: number) { return (await this.client.post(`/inventory-mail/schedules/${id}/run`)).data; }
  async getMailHistory(params?: any) { return (await this.client.get('/inventory-mail/history', { params })).data; }

  // ACTIVITY RULES (existing)
  async getActivityRules() { return (await this.client.get('/activity-rules')).data; }
  async createActivityRule(type: string, data: any) { return (await this.client.post(`/activity-rules/${type}`, data)).data; }
  async updateActivityRule(type: string, data: any) { return (await this.client.patch(`/activity-rules/${type}`, data)).data; }
  async deleteActivityRule(type: string, id: string) { return (await this.client.delete(`/activity-rules/${type}/${id}`)).data; }

  // WEEKLY
  async getWeeklyEntries() { return (await this.client.get('/weekly-entries')).data; }
  async getWeeklySummary() { return (await this.client.get('/weekly-utilization-summary')).data; }

  // INBOUND BOOKINGS (detailed booking module with QR, approval, etc.)
  async createInboundBooking(data: any) { return (await this.client.post('/inbound-bookings', data)).data; }
  async getInboundBookings(params?: any) { return (await this.client.get('/inbound-bookings', { params })).data; }
  async getInboundBooking(id: number) { return (await this.client.get(`/inbound-bookings/${id}`)).data; }
  async updateInboundBooking(id: number, data: any) { return (await this.client.put(`/inbound-bookings/${id}`, data)).data; }
  async approveInboundBooking(id: number, notes?: string) { return (await this.client.post(`/inbound-bookings/${id}/approve`, { notes })).data; }
  async rejectInboundBooking(id: number, reason: string) { return (await this.client.post(`/inbound-bookings/${id}/reject`, { rejection_reason: reason })).data; }
  async cancelInboundBooking(id: number) { return (await this.client.post(`/inbound-bookings/${id}/cancel`)).data; }
  async updateInboundBookingStatus(id: number, status: string, notes?: string) { return (await this.client.put(`/inbound-bookings/${id}/status`, { status, notes })).data; }
  async getInboundBookingStats() { return (await this.client.get('/inbound-bookings/stats')).data; }
  async getInboundBookingQR(id: number) { return (await this.client.get(`/inbound-bookings/${id}/qr`, { responseType: 'blob' })).data; }
  async getInboundBookingCard(id: number) { return (await this.client.get(`/inbound-bookings/${id}/card`)).data; }
  async scanQRToken(token: string) { return (await this.client.get(`/inbound-bookings/scan/${token}`)).data; }
  async truckIn(data: any) { return (await this.client.post('/inbound-bookings/truck-in', data)).data; }
  async truckOut(data: any) { return (await this.client.post('/inbound-bookings/truck-out', data)).data; }

  // OUTBOUND
  async getOutbound(params?: any) { return (await this.client.get('/outbound', { params })).data; }
  async getOutboundStats() { return (await this.client.get('/outbound/stats')).data; }
  async getOutboundOrderSummary(params?: any) { return (await this.client.get('/outbound/order-summary', { params })).data; }
  async getOutboundWorkPackages(params?: any) { return (await this.client.get('/outbound/work-packages', { params })).data; }
  async getOutboundToday() { return (await this.client.get('/outbound/today')).data; }
  async getOutboundTrend(days = 12) { return (await this.client.get('/outbound/trend', { params: { days } })).data; }
  async getOutboundPalletsDaily(params?: any) { return (await this.client.get('/outbound/pallets-daily', { params })).data; }
  // Canonical PAPNSTS-backed daily orders ledger — drives Total Orders / pipeline / status KPIs.
  async getOutboundOrdersDaily(params?: any) { return (await this.client.get('/outbound/orders-daily', { params })).data; }
  // Bulk-fetch — replaces 5 individual /outbound/* round-trips with one.
  async getOutboundBundle(params?: any) { return (await this.client.get('/outbound/bundle', { params })).data; }

  // INBOUND (real data endpoints)
  async getInboundGRSummary(params?: any) { return (await this.client.get('/inbound/gr-summary', { params })).data; }
  async getInboundProcessStatus(params?: any) { return (await this.client.get('/inbound/process-status', { params })).data; }
  async getInboundTruckTypes(params?: any) { return (await this.client.get('/inbound/truck-types', { params })).data; }
  async getInboundToday() { return (await this.client.get('/inbound/today')).data; }
  async getInboundPipeline() { return (await this.client.get('/inbound/pipeline')).data; }

  // HALLEY AI
  async getHalleyStats() { return (await this.client.get('/halley/stats')).data; }
  async getHalleyInsights() { return (await this.client.get('/halley/insights')).data; }

  // CUSTOMER CONTACTS
  async getCustomerContacts() { return (await this.client.get('/customer-contacts')).data; }
  async getCustomerContact(code: string) { return (await this.client.get(`/customer-contacts/${code}`)).data; }
  async createCustomerContact(data: any) { return (await this.client.post('/customer-contacts', data)).data; }
  async updateCustomerContact(code: string, data: any) { return (await this.client.put(`/customer-contacts/${code}`, data)).data; }
  async deleteCustomerContact(code: string) { return (await this.client.delete(`/customer-contacts/${code}`)).data; }
  async getMyContactInfo() { return (await this.client.get('/customer-contacts/my/info')).data; }

  // CUSTOMER SATISFACTION RATINGS
  async submitRating(data: any) { return (await this.client.post('/ratings', data)).data; }
  async getMyRatings() { return (await this.client.get('/ratings/my')).data; }
  async getAllRatings() { return (await this.client.get('/ratings/all')).data; }
  async getRatingsSummary() { return (await this.client.get('/ratings/summary')).data; }
  async getCustomerRatings(code: string) { return (await this.client.get(`/ratings/customer/${code}`)).data; }
  async respondToRating(id: number, response: string) { return (await this.client.post(`/ratings/${id}/respond`, { admin_response: response })).data; }
  async deleteRating(id: number) { return (await this.client.delete(`/ratings/${id}`)).data; }

  // TRANSPORT LIVE DATA (from DB2 truck timings — SSRA10/SSRA13 parsed)
  async getTransportLiveStats(params?: any) { return (await this.client.get('/transport/live-stats', { params })).data; }
  async getTransportDocks(warehouse_code?: string) { return (await this.client.get('/transport/docks', { params: { warehouse_code } })).data; }
  async getInboundTruckTimings(params?: any) { return (await this.client.get('/inbound/truck-timings', { params })).data; }
  async getInboundTruckDurations(params?: any) { return (await this.client.get('/inbound/truck-durations', { params })).data; }
  async getInboundPipelineDetail(params?: any) { return (await this.client.get('/inbound/pipeline-detail', { params })).data; }
  async getInOutTrend(days = 30) { return (await this.client.get('/in-out-trend', { params: { days } })).data; }

  // BOOKINGS — warehouse truck reconciliation (DB2 cross-reference)
  async getWarehouseTrucks(params?: any) { return (await this.client.get('/inbound-bookings/warehouse-trucks', { params })).data; }
  async getBookingTruckActivity(params?: any) { return (await this.client.get('/inbound-bookings/truck-activity', { params })).data; }
  async syncBookingFromWarehouse(bookingId: number) { return (await this.client.post(`/inbound-bookings/${bookingId}/sync-from-warehouse`)).data; }

  // DASHBOARD & MONITORING
  async getDashboardMonthly(year?: number, month?: number) { return (await this.client.get('/dashboard/monthly', { params: { year, month } })).data; }
  async getActivityFeed(limit = 50, type?: string) { return (await this.client.get('/activity-feed', { params: { limit, type } })).data; }
  async getMonitoringDaily() { return (await this.client.get('/monitoring/daily')).data; }

  // ALERTS & HEALTH
  async getAlertExpiry() { return (await this.client.get('/alerts/expiry')).data; }
  async getAlertCapacity() { return (await this.client.get('/alerts/capacity')).data; }
  async getAlertInbound() { return (await this.client.get('/alerts/inbound')).data; }
  async getAlertDiscrepancies() { return (await this.client.get('/alerts/discrepancies')).data; }
  async getAlertAll() { return (await this.client.get('/alerts/all')).data; }
  async getSystemHealth() { return (await this.client.get('/alerts/health')).data; }
  async connectOutlook(data: any) { return (await this.client.post('/alerts/email/connect-outlook', data)).data; }
  async testOutlook() { return (await this.client.get('/alerts/email/test')).data; }
  async getRecentEmails() { return (await this.client.get('/alerts/email/recent')).data; }

  // STOCK & AGING
  async getStockMovement(params?: any) { return (await this.client.get('/stock-movement', { params })).data; }
  async getDailyReconciliation(params?: any) { return (await this.client.get('/daily-reconciliation', { params })).data; }
  async getHoldingAging(params?: any) { return (await this.client.get('/holding-aging', { params })).data; }
  async getDGSummary() { return (await this.client.get('/inventory/dg-summary')).data; }
  async getInventoryUOMGraph(params?: any) { return (await this.client.get('/inventory/uom-graph', { params })).data; }
  // ABC velocity classification (Pareto A/B/C on 90-day outbound movements).
  async getInventoryABC() { return (await this.client.get('/inventory/abc')).data; }

  // UTILIZATION UOM
  async getCustomerUOMBreakdown(params?: any) { return (await this.client.get('/utilization/customer-uom-breakdown', { params })).data; }
  async getCustomerItems(params?: any) { return (await this.client.get('/utilization/customer-items', { params })).data; }

  // ═══ TEAMS + MEMBERSHIPS (Phase 1/2) ═══
  async listTeams(all = false) {
    return (await this.client.get('/teams', { params: { all } })).data;
  }
  async getTeam(teamId: number) {
    return (await this.client.get(`/teams/${teamId}`)).data;
  }
  async listTeamMembers(teamId: number) {
    return (await this.client.get(`/teams/${teamId}/members`)).data;
  }
  async createTeam(data: {
    code: string; name: string; description?: string; team_type?: string;
    role_name?: string; customer_code?: string;
    warehouse_codes?: string[]; is_chat_enabled?: boolean;
  }) { return (await this.client.post('/teams', data)).data; }
  async updateTeam(teamId: number, data: any) {
    return (await this.client.put(`/teams/${teamId}`, data)).data;
  }
  async addTeamMember(teamId: number, userId: number, membershipRole = 'member') {
    return (await this.client.post(`/teams/${teamId}/members`, {
      user_id: userId, membership_role: membershipRole,
    })).data;
  }
  async updateTeamMember(teamId: number, userId: number, data: any) {
    return (await this.client.put(`/teams/${teamId}/members/${userId}`, data)).data;
  }
  async removeTeamMember(teamId: number, userId: number) {
    return (await this.client.delete(`/teams/${teamId}/members/${userId}`)).data;
  }
  async listProjectLeaderCustomers(userId: number) {
    return (await this.client.get(`/teams/project-leaders/${userId}/customers`)).data;
  }
  async assignProjectLeaderCustomer(data: {
    user_id: number; customer_code: string; warehouse_codes?: string[];
  }) { return (await this.client.post('/teams/project-leaders', data)).data; }
  async unassignProjectLeaderCustomer(plcId: number) {
    return (await this.client.delete(`/teams/project-leaders/${plcId}`)).data;
  }
  async getTeamRooms() {
    return (await this.client.get('/chat/team-rooms')).data;
  }

  // ═══ CONTAINER DRILLDOWN (cross-DB trace) ═══
  async searchContainers(q: string, limit = 20) {
    return (await this.client.get('/containers/search', { params: { q, limit } })).data;
  }
  async getContainerTrace(containerNumber: string) {
    return (await this.client.get(`/containers/${encodeURIComponent(containerNumber)}/trace`)).data;
  }
  async getContainerAttachments(containerNumber: string) {
    return (await this.client.get(`/containers/${encodeURIComponent(containerNumber)}/attachments`)).data;
  }

  // STORAGE TYPE MAP (chamber -> category/flags/description) — Azure source of truth
  async getStorageTypes() { return (await this.client.get('/lookups/storage-types')).data; }
  async upsertStorageType(data: any) { return (await this.client.put('/lookups/storage-types', data)).data; }
  async deleteStorageType(fullCode: string) { return (await this.client.delete(`/lookups/storage-types/${encodeURIComponent(fullCode)}`)).data; }

  // PROJECT LEADERS
  async getProjectLeaders(params?: any) { return (await this.client.get('/project-leaders', { params })).data; }
  async getMyProjectLeaders() { return (await this.client.get('/project-leaders/my')).data; }
  async createProjectLeader(data: any) { return (await this.client.post('/project-leaders', data)).data; }
  async updateProjectLeader(id: number, data: any) { return (await this.client.put(`/project-leaders/${id}`, data)).data; }
  async deleteProjectLeader(id: number) { return (await this.client.delete(`/project-leaders/${id}`)).data; }
  async uploadProjectLeaderImage(formData: FormData) {
    return (await this.client.post('/project-leaders/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  }

  // DRILL DOWN
  async getCustomersTop(limit = 10) { return (await this.client.get('/customers/top', { params: { limit } })).data; }
  async getWarehouseDetails(code: string) { return (await this.client.get(`/warehouses/${code}/details`)).data; }
  async getWarehouseCustomers(code: string) { return (await this.client.get(`/warehouses/${code}/customers`)).data; }
  async getCustomerDetail(name: string) { return (await this.client.get(`/customers/${encodeURIComponent(name)}/detail`)).data; }
  async getInboundRange(params?: any) { return (await this.client.get('/inbound/range', { params })).data; }
  // Bulk-fetch — replaces 6 individual /inbound/* round-trips with one.
  async getInboundBundle(params?: any) { return (await this.client.get('/inbound/bundle', { params })).data; }
  async getInboundPalletsDaily(params?: any) { return (await this.client.get('/inbound/pallets-daily', { params })).data; }
  // Canonical PWAEKOP-backed daily GR ledger — drives Total GR / pipeline / status KPIs.
  async getInboundGRDaily(params?: any) { return (await this.client.get('/inbound/gr-daily', { params })).data; }

  // ═══ ANNOUNCEMENTS (Dashboard Slider) ═══
  async getAnnouncements(params?: any) { return (await this.client.get('/announcements', { params })).data; }
  async getAnnouncement(id: number) { return (await this.client.get(`/announcements/${id}`)).data; }
  async createAnnouncement(data: any) { return (await this.client.post('/announcements', data)).data; }
  async updateAnnouncement(id: number, data: any) { return (await this.client.put(`/announcements/${id}`, data)).data; }
  async deleteAnnouncement(id: number) { return (await this.client.delete(`/announcements/${id}`)).data; }
  async uploadAnnouncementImage(formData: FormData) {
    return (await this.client.post('/announcements/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  }
  async uploadAnnouncementVideo(formData: FormData) {
    return (await this.client.post('/announcements/upload-video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  }
  async getAnnouncementTicker() {
    return (await this.client.get('/announcements/ticker')).data;
  }
  async getAnnouncementStats() { return (await this.client.get('/announcements/admin/stats')).data; }
  async getCustomersForTargeting() { return (await this.client.get('/announcements/admin/customers')).data; }

  // ═══ ANNOUNCEMENT CATEGORIES (User-defined) ═══
  async getAnnouncementCategories(params?: { include_shared?: boolean }) {
    return (await this.client.get('/announcements/categories', { params })).data;
  }
  async createAnnouncementCategory(data: any) {
    return (await this.client.post('/announcements/categories', data)).data;
  }
  async updateAnnouncementCategory(id: number, data: any) {
    return (await this.client.put(`/announcements/categories/${id}`, data)).data;
  }
  async deleteAnnouncementCategory(id: number) {
    return (await this.client.delete(`/announcements/categories/${id}`)).data;
  }

  // ═══ ANNOUNCEMENT TEMPLATES (User-defined) ═══
  async getAnnouncementTemplates(params?: { category_id?: number; include_shared?: boolean }) {
    return (await this.client.get('/announcements/templates', { params })).data;
  }
  async createAnnouncementTemplate(data: any) {
    return (await this.client.post('/announcements/templates', data)).data;
  }
  async updateAnnouncementTemplate(id: number, data: any) {
    return (await this.client.put(`/announcements/templates/${id}`, data)).data;
  }
  async deleteAnnouncementTemplate(id: number) {
    return (await this.client.delete(`/announcements/templates/${id}`)).data;
  }
  async useAnnouncementTemplate(id: number) {
    return (await this.client.post(`/announcements/templates/${id}/use`)).data;
  }

  // ═══ TEAM CHAT ═══
  async getChatConversations() { return (await this.client.get('/chat/conversations')).data; }
  async getChatConversation(id: number) { return (await this.client.get(`/chat/conversations/${id}`)).data; }
  async createChatConversation(data: any) { return (await this.client.post('/chat/conversations', data)).data; }
  async getChatMessages(convId: number, params?: any) { return (await this.client.get(`/chat/conversations/${convId}/messages`, { params })).data; }
  async sendChatMessage(convId: number, data: any) { return (await this.client.post(`/chat/conversations/${convId}/messages`, data)).data; }
  async editChatMessage(msgId: number, content: string) { return (await this.client.put(`/chat/messages/${msgId}`, { content })).data; }
  async deleteChatMessage(msgId: number) { return (await this.client.delete(`/chat/messages/${msgId}`)).data; }
  async getChatGroups() { return (await this.client.get('/chat/groups')).data; }
  async createChatGroup(data: any) { return (await this.client.post('/chat/groups', data)).data; }
  async updateChatGroup(id: number, data: any) { return (await this.client.put(`/chat/groups/${id}`, data)).data; }
  async addChatGroupMembers(groupId: number, userIds: number[]) { return (await this.client.post(`/chat/groups/${groupId}/members`, { user_ids: userIds })).data; }
  async removeChatGroupMember(groupId: number, memberId: number) { return (await this.client.delete(`/chat/groups/${groupId}/members/${memberId}`)).data; }
  async getChatContacts() { return (await this.client.get('/chat/contacts')).data; }
  async markChatRead(convId: number) { return (await this.client.post(`/chat/conversations/${convId}/read`)).data; }
  async getChatUnreadCount() { return (await this.client.get('/chat/unread-count')).data; }

  // Chat Attachments & Email Forwarding
  async uploadChatAttachment(conversationId: number, formData: FormData) {
    return (await this.client.post(`/chat/conversations/${conversationId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    })).data;
  }
  async downloadChatAttachment(attachmentId: number) {
    return (await this.client.get(`/chat/attachments/${attachmentId}/download`, { responseType: 'blob' })).data;
  }
  async sendChatEmail(data: {
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
    conversation_id?: number;
    message_ids?: number[];
    attachment_ids?: number[];
    include_chat_context?: boolean;
  }) { return (await this.client.post('/chat/email/send', data)).data; }

  // ═══ QUOTATION GENERATION ═══
  async generateQuotation(formData: FormData) {
    return (await this.client.post('/quotation/generate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    })).data;
  }

  // ═══ CUSTOMER CONTRACTS & ALERTS ═══
  async getContracts(params?: any) { return (await this.client.get('/customer-contracts', { params })).data; }
  async getContract(id: number) { return (await this.client.get(`/customer-contracts/${id}`)).data; }
  async createContract(data: any) { return (await this.client.post('/customer-contracts', data)).data; }
  async updateContract(id: number, data: any) { return (await this.client.put(`/customer-contracts/${id}`, data)).data; }
  async deleteContract(id: number) { return (await this.client.delete(`/customer-contracts/${id}`)).data; }
  async getContractStats() { return (await this.client.get('/customer-contracts/stats')).data; }
  async importContracts(contracts: any[]) { return (await this.client.post('/customer-contracts/import', contracts)).data; }
  async getContractAlerts() { return (await this.client.get('/customer-contracts/alerts')).data; }
  async createContractAlert(data: any) { return (await this.client.post('/customer-contracts/alerts', data)).data; }
  async updateContractAlert(id: number, data: any) { return (await this.client.put(`/customer-contracts/alerts/${id}`, data)).data; }
  async deleteContractAlert(id: number) { return (await this.client.delete(`/customer-contracts/alerts/${id}`)).data; }
  async checkContractAlerts() { return (await this.client.post('/customer-contracts/alerts/check')).data; }

  // ═══ WEEKLY SNAPSHOTS ═══
  async getSnapshotWeeks() { return (await this.client.get('/snapshots/weeks')).data; }
  async checkSnapshot(week: number, year: number) { return (await this.client.get(`/snapshots/check/${week}/${year}`)).data; }
  async getSnapshotOverview(params?: any) { return (await this.client.get('/snapshots/overview', { params })).data; }
  async getSnapshotWarehouses(params?: any) { return (await this.client.get('/snapshots/warehouses', { params })).data; }
  async getSnapshotClients(params?: any) { return (await this.client.get('/snapshots/clients', { params })).data; }
  async getSnapshotUOM(params?: any) { return (await this.client.get('/snapshots/uom', { params })).data; }
  async captureSnapshot(data: any) { return (await this.client.post('/snapshots/capture', data)).data; }

  // ═══ CUSTOMER GROUPS & TEAMS INTEGRATION ═══
  async getCustomerGroups() { return (await this.client.get('/customer-groups')).data; }
  async upsertCustomerGroup(data: any) { return (await this.client.post('/customer-groups', data)).data; }
  async deleteCustomerGroup(id: number) { return (await this.client.delete(`/customer-groups/${id}`)).data; }
  async getTeamsConfig() { return (await this.client.get('/teams/config')).data; }
  async saveTeamsConfig(data: any) { return (await this.client.post('/teams/configure', data)).data; }
  async sendTeamsWeeklyReminders() { return (await this.client.post('/teams/send-weekly-reminders')).data; }
  async sendTeamsSummary(data: any) { return (await this.client.post('/teams/send-summary', data)).data; }

  // ═══ EXCEL COLUMN TEMPLATES ═══
  async getExcelTemplateFields() { return (await this.client.get('/excel-templates/fields')).data; }
  async getExcelTemplates(params?: any) { return (await this.client.get('/excel-templates', { params })).data; }
  async getExcelTemplate(id: number) { return (await this.client.get(`/excel-templates/${id}`)).data; }
  async resolveExcelTemplate(customerCode: string) { return (await this.client.get(`/excel-templates/resolve/${customerCode}`)).data; }
  async createExcelTemplate(data: any) { return (await this.client.post('/excel-templates', data)).data; }
  async updateExcelTemplate(id: number, data: any) { return (await this.client.put(`/excel-templates/${id}`, data)).data; }
  async deleteExcelTemplate(id: number) { return (await this.client.delete(`/excel-templates/${id}`)).data; }

  // ═══ WAREHOUSE MANAGEMENT (DB2 direct) ═══
  async getWarehouseLocations(warehouse: string) { return (await this.client.get('/warehouse', { params: { warehouse } })).data; }
  async getVirtualStorage(warehouse: string) { return (await this.client.get('/warehouse/virtual', { params: { warehouse } })).data; }
  async getCustomerStorageLocations(warehouse: string) { return (await this.client.get('/warehouse/customer-storage-locations', { params: { warehouse } })).data; }
  async getCustomerStorageMerged(params: any) { return (await this.client.get('/warehouse/customer-storage-merged', { params })).data; }
  async getBinInventory(params: any) { return (await this.client.get('/warehouse/bin-inventory', { params })).data; }
  async getBinInventoryStats(warehouse: string) { return (await this.client.get('/warehouse/bin-inventory/stats', { params: { warehouse } })).data; }
  async getBinInventoryByWarehouse() { return (await this.client.get('/warehouse/bin-inventory/by-warehouse')).data; }

  // ═══ CUSTOMER REGISTRATION (Admin) ═══
  async getPendingRegistrations(params?: any) { return (await this.client.get('/customer/registrations', { params })).data; }

  /**
   * Approve a pending registration. Phase-2 body REQUIRES:
   *   - bmfirm_selections: string[]            (DB2 trading names, ≥1)
   *   - scope_bindings: ScopeBinding[]         (warehouse + optional project/storage, ≥1)
   *   - module_permissions: ModulePermissions? (8 BIT flags, defaults conservative)
   */
  async approveRegistration(
    id: number,
    body: {
      notes?: string;
      bmfirm_selections: string[];
      scope_bindings: Array<{
        warehouse_code: string;
        project_code?: string | null;
        storage_location?: string | null;
      }>;
      module_permissions?: {
        can_view_dashboard?: boolean;
        can_view_inbound?: boolean;
        can_view_outbound?: boolean;
        can_view_inventory?: boolean;
        can_view_transport?: boolean;
        can_use_halley?: boolean;
        can_manage_users?: boolean;
        can_view_expiry?: boolean;
      };
    },
  ) {
    return (await this.client.post(`/customer/registrations/${id}/approve`, body)).data;
  }

  async rejectRegistration(id: number, reason: string) { return (await this.client.post(`/customer/registrations/${id}/reject`, { rejection_reason: reason })).data; }

  // ═══ DB2-READ-ONLY LOOKUPS (for approval combobox cascade) ═══
  async searchBMFirms(q: string, limit = 20) {
    return (await this.client.get('/lookups/bmfirm-search', { params: { q, limit } })).data;
  }

  // ── Alert rules (advanced alerting) ─────────────────────────────────
  async listAlertRules(onlyMine = true) {
    return (await this.client.get('/alert-rules', { params: { only_mine: onlyMine } })).data;
  }
  async createAlertRule(body: any) {
    return (await this.client.post('/alert-rules', body)).data;
  }
  async updateAlertRule(id: number, body: any) {
    return (await this.client.put(`/alert-rules/${id}`, body)).data;
  }
  async deleteAlertRule(id: number) {
    return (await this.client.delete(`/alert-rules/${id}`)).data;
  }
  async toggleAlertRule(id: number) {
    return (await this.client.post(`/alert-rules/${id}/toggle`)).data;
  }
  async evaluateAlertRulesNow() {
    return (await this.client.post('/alert-rules/evaluate-now')).data;
  }
  async testAlertRule(id: number, sendEmail = false) {
    return (await this.client.post(
      `/alert-rules/${id}/test`,
      null,
      { params: { send_email: sendEmail } },
    )).data;
  }
  async getWarehousesForBMFirms(bmfirms: string[]) {
    return (await this.client.get('/lookups/warehouses', { params: { bmfirms: bmfirms.join(',') } })).data;
  }
  async getProjectsForWarehouses(bmfirms: string[], warehouses: string[]) {
    return (await this.client.get('/lookups/projects', {
      params: { bmfirms: bmfirms.join(','), warehouses: warehouses.join(',') },
    })).data;
  }
  async getUnmappedBMFirms(includeResolved = false) {
    return (await this.client.get('/lookups/unmapped-bmfirms', {
      params: { include_resolved: includeResolved },
    })).data;
  }
  async refreshUnmappedBMFirms() {
    return (await this.client.post('/lookups/refresh-unmapped')).data;
  }
  async resolveUnmappedBMFirm(id: number) {
    return (await this.client.post(`/lookups/unmapped-bmfirms/${id}/resolve`)).data;
  }

  // ═══ CUSTOMER SCOPE MANAGEMENT (post-approval) ═══
  async getCustomerScope(customerCode: string) {
    return (await this.client.get(`/customer/${encodeURIComponent(customerCode)}/scope`)).data;
  }
  async updateCustomerMappings(
    customerCode: string,
    body: { add_bmfirms?: string[]; remove_bmfirms?: string[]; notes?: string },
  ) {
    return (await this.client.patch(`/customer/${encodeURIComponent(customerCode)}/mappings`, body)).data;
  }
  async updateCustomerScope(
    customerCode: string,
    body: {
      add_bindings?: Array<{ warehouse_code: string; project_code?: string | null; storage_location?: string | null }>;
      revoke_binding_ids?: number[];
      notes?: string;
    },
  ) {
    return (await this.client.patch(`/customer/${encodeURIComponent(customerCode)}/scope`, body)).data;
  }

  // ═══ UTILIZATION (DB2 direct) ═══
  async getUtilizationWeekComparison(params?: any) { return (await this.client.get('/utilization/week-comparison', { params })).data; }
  async getUtilizationTopClients(params?: any) { return (await this.client.get('/utilization/top-clients', { params })).data; }
  async getUtilizationAreasByWarehouse(params?: any) { return (await this.client.get('/utilization/areas-by-warehouse', { params })).data; }

  // ═══ HOLDING / AGING ANALYSIS ═══
  async getHoldingAgingBreakdown(params?: any) { return (await this.client.get('/holding-aging/breakdown', { params })).data; }
  async getHoldingAgingCustomer(name: string) { return (await this.client.get(`/holding-aging/customer/${encodeURIComponent(name)}`)).data; }
}

const api = new ApiService();
export default api;
